// Auth context — wraps Google Sign-In state so any component can access the
// current user and their ID token. The token is what the backend verifies via
// google.oauth2.id_token.verify_oauth2_token in marker_api.py.
//
// We use @react-oauth/google's useGoogleLogin with implicit flow for the
// initial sign-in, and manually decode the JWT to extract email/name/picture
// for local UI display. The BACKEND does the actual signature verification
// on every /api/* call — the client-side decode is display-only.
//
// Token refresh: Google ID tokens expire after 1 hour. We register a
// refresher with api.ts that fires on any 401 with a "token expired"
// detail. The refresher asks Google Identity Services to silently
// re-issue a token via google.accounts.id.initialize+prompt (which
// works without UI as long as the user's Google session is still
// active and they've granted consent). If refresh fails, we sign out
// so the user hits the sign-in screen rather than a broken app.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { AuthUser } from './types'
import { setAuthToken, setTokenRefresher } from './api'

interface AuthContextValue {
  user: AuthUser | null
  signIn: (idToken: string) => void
  signOut: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

const STORAGE_KEY = 'marker.auth.v1'
const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined
const REFRESH_TIMEOUT_MS = 6_000  // How long we wait for GIS to respond

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const [, payloadB64] = token.split('.')
    if (!payloadB64) return null
    // JWT payloads are base64url-encoded
    const b64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    return JSON.parse(atob(padded))
  } catch {
    return null
  }
}

function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token)
  if (!payload || typeof payload.exp !== 'number') return true
  return payload.exp * 1000 < Date.now()
}

// Grab a fresh ID token silently from Google Identity Services.
// Requires the GIS script (loaded by <GoogleOAuthProvider>) and a
// still-active Google session for the same account. Returns null if
// GIS isn't ready, the prompt was suppressed, or Google didn't
// re-issue within REFRESH_TIMEOUT_MS.
async function silentGoogleRefresh(): Promise<string | null> {
  if (!CLIENT_ID) return null

  // Access the GIS runtime that @react-oauth/google's provider loaded.
  const g = (window as unknown as {
    google?: { accounts?: { id?: {
      initialize: (config: unknown) => void
      prompt: (cb?: (n: unknown) => void) => void
      cancel: () => void
    } } }
  }).google?.accounts?.id
  if (!g) return null

  return new Promise<string | null>((resolve) => {
    let settled = false
    const finish = (val: string | null) => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      resolve(val)
    }
    const timer = window.setTimeout(() => {
      // Cancel any pending prompt so we don't leave UI dangling.
      try { g.cancel() } catch { /* noop */ }
      finish(null)
    }, REFRESH_TIMEOUT_MS)

    try {
      g.initialize({
        client_id: CLIENT_ID,
        auto_select: true,
        itp_support: true,
        callback: (response: { credential?: string }) => {
          finish(response?.credential || null)
        },
      })
      g.prompt()
    } catch {
      finish(null)
    }
  })
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  // Held in a ref so the refresher closure (registered once with
  // api.ts) can always reach the latest signOut without stale-closure
  // bugs from re-renders.
  const signOutRef = useRef<() => void>(() => {})

  // Restore session on mount
  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    try {
      const stored: AuthUser = JSON.parse(raw)
      if (stored.idToken && !isTokenExpired(stored.idToken)) {
        setUser(stored)
        setAuthToken(stored.idToken)
      } else {
        localStorage.removeItem(STORAGE_KEY)
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [])

  const signIn = useCallback((idToken: string) => {
    const payload = decodeJwtPayload(idToken)
    if (!payload || typeof payload.email !== 'string') {
      console.error('Invalid ID token — could not extract email')
      return
    }
    const newUser: AuthUser = {
      email: payload.email as string,
      name: payload.name as string | undefined,
      picture: payload.picture as string | undefined,
      idToken,
    }
    setUser(newUser)
    setAuthToken(idToken)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newUser))
  }, [])

  const signOut = useCallback(() => {
    setUser(null)
    setAuthToken(null)
    localStorage.removeItem(STORAGE_KEY)
  }, [])
  // Keep the ref pointed at the latest signOut so the refresher
  // registered below can call the right one after re-renders.
  signOutRef.current = signOut

  // Register the token-refresh callback with api.ts once. api.ts calls
  // this whenever a request 401s with a token-expired error.
  useEffect(() => {
    const refresh = async (): Promise<string | null> => {
      const newToken = await silentGoogleRefresh()
      if (!newToken) {
        // Silent refresh failed — sign out so the user gets a clean
        // sign-in prompt instead of an infinite 401 loop.
        signOutRef.current()
        return null
      }
      // Update local state + storage so the new token persists across
      // reloads. Reuse signIn's payload-decode + storage logic.
      const payload = decodeJwtPayload(newToken)
      if (!payload || typeof payload.email !== 'string') {
        signOutRef.current()
        return null
      }
      const refreshed: AuthUser = {
        email: payload.email as string,
        name: payload.name as string | undefined,
        picture: payload.picture as string | undefined,
        idToken: newToken,
      }
      setUser(refreshed)
      setAuthToken(newToken)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(refreshed))
      return newToken
    }
    setTokenRefresher(refresh)
    return () => setTokenRefresher(null)
  }, [])

  return (
    <AuthContext.Provider value={{ user, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
